/**
 * @fileoverview Data agents — frontend display registry
 * @module jobs/phases/gather/summarization/agentRegistry
 *
 * Purpose:
 * - Single source of truth on the frontend for everything a Data-section
 *   summarization agent needs for navigation and rendering UI:
 *     - manifest `id` (e.g. "sum_corporate") — primary key in Convex
 *     - URL `slug` (e.g. "corporate") — used in /summaries/$agent routes
 *     - human-readable `label` (mirrors uiMetadata.ts)
 *     - lucide `icon` shown on the agent card and sidebar sub-item
 *
 * The manifest names live in `convex/jobs/summarization/agents/uiMetadata.ts`
 * (which is the Convex-side mirror); this file augments them with frontend-
 * only display concerns (icons + URL slugs) so the sidebar, AgentCard, and
 * the slug-based router can all share them.
 *
 * Used by:
 * - AgentCard (per-row icon)
 * - app-sidebar (sub-item navigation under "Data")
 * - summaries.$agent route (slug → manifest lookup)
 * - SummarizationView (card click → navigate to slug route)
 */

import {
  Puzzle,
  LayoutPanelTop,
  TextSearch,
  Users,
  CreditCard,
  Truck,
  BadgeDollarSign,
  FileSearch,
  Contact,
  Banknote,
  HandCoins,
  Landmark,
  type LucideIcon,
} from "lucide-react";
import { caseTypeFromAppointmentType } from "@/lib/precedent-templates";

export interface DataAgentEntry {
  /** Manifest id (e.g. "sum_corporate"). Primary key in summarizationTasks. */
  id: string;
  /** URL slug used in /dashboard/jobs/$jobId/summaries/$agent. */
  slug: string;
  /** Human-readable label (matches manifest.name post-rename). */
  label: string;
  /**
   * Regime-aware override for {@link label} on personal-insolvency (bankrupt
   * estate) jobs. The Corporate agent maps the same "who is connected to the
   * estate" analysis onto an individual, so it reads as "Structure" there.
   * Falls back to {@link label} when unset. See {@link dataAgentLabel}.
   */
  bankruptcyLabel?: string;
  /** Lucide icon shown on the agent card and sidebar sub-item. */
  icon: LucideIcon;
  /**
   * True for agents whose panel supports hand-entered rows and whose view can
   * be opened before the agent has ever run. Drives the "Enter manually"
   * affordance on the waiting placeholders, so a practitioner can record
   * entries they already know about without waiting for documents.
   */
  supportsManualEntry?: boolean;
  /**
   * True for agents that stay visible on pre-appointment (Flow A) jobs.
   * Data as a whole is shown in that state so practitioners can watch the
   * Corporate agent finish; every other Data agent stays hidden until the
   * job is converted.
   */
  visibleOnPreAppointment?: boolean;
  /**
   * Hidden entirely on personal-insolvency (bankrupt estate) jobs — the
   * agent's analysis is company-specific with no individual analogue (e.g.
   * board minutes & reports). Gated everywhere via {@link dataAgentsForJob}
   * and {@link isDataAgentVisibleOnJob}.
   */
  hiddenForBankruptcy?: boolean;
}

/**
 * Ordered list of all Data agents. Order here drives sidebar sub-item order
 * and any other "iterate every agent" UI affordance.
 */
export const DATA_AGENTS: ReadonlyArray<DataAgentEntry> = [
  // Foundation
  {
    id: "sum_indicator",
    slug: "complexity-indicator",
    label: "Complexity Indicator",
    icon: Puzzle,
  },
  {
    id: "sum_corporate",
    slug: "corporate",
    label: "Corporate",
    bankruptcyLabel: "Structure",
    icon: LayoutPanelTop,
    visibleOnPreAppointment: true,
  },
  {
    id: "sum_board_reports",
    slug: "board-reports",
    label: "Board Minutes & Reports",
    icon: TextSearch,
    hiddenForBankruptcy: true,
  },
  {
    id: "sum_related_parties",
    slug: "related-parties",
    label: "Related Parties",
    icon: Users,
  },
  // Financial
  {
    id: "sum_bank_transactions",
    slug: "bank-transactions",
    label: "Bank Transactions",
    icon: CreditCard,
  },
  // Assets
  { id: "sum_asset_list", slug: "assets", label: "Assets", icon: Truck },
  {
    id: "sum_asset_sales",
    slug: "asset-sales",
    label: "Asset Sales & Purchases",
    icon: BadgeDollarSign,
  },
  // Liabilities
  {
    id: "sum_security_interests",
    slug: "security-interests",
    label: "Security Interests",
    icon: FileSearch,
  },
  {
    id: "sum_employment",
    slug: "employment",
    label: "Preferred Creditors",
    icon: Contact,
    supportsManualEntry: true,
  },
  {
    id: "sum_general_liabilities",
    slug: "creditors",
    label: "Creditors",
    icon: Banknote,
    supportsManualEntry: true,
  },
  { id: "sum_loans", slug: "loans", label: "Loans", icon: HandCoins },
  { id: "sum_ato_sro", slug: "ato-sro", label: "ATO & SRO", icon: Landmark },
];

/**
 * Display label for a Data agent, adapted to the job's regime. On a personal-
 * insolvency (bankrupt estate) job the agent's `bankruptcyLabel` wins when set;
 * everything else (including all corporate appointments) uses `label`.
 */
export function dataAgentLabel(
  entry: DataAgentEntry,
  isBankruptcy: boolean,
): string {
  return isBankruptcy && entry.bankruptcyLabel
    ? entry.bankruptcyLabel
    : entry.label;
}

/** agentId → entry. */
export const DATA_AGENTS_BY_ID: ReadonlyMap<string, DataAgentEntry> = new Map(
  DATA_AGENTS.map((a) => [a.id, a]),
);

/**
 * Regime-aware label looked up by manifest id — for surfaces (e.g. the Data
 * overview cards) that key off the Convex manifest name rather than a registry
 * entry. Returns undefined when the id is unknown so the caller can fall back.
 */
export function dataAgentLabelById(
  agentId: string,
  isBankruptcy: boolean,
): string | undefined {
  const entry = DATA_AGENTS_BY_ID.get(agentId);
  return entry ? dataAgentLabel(entry, isBankruptcy) : undefined;
}

/** slug → entry. */
export const DATA_AGENTS_BY_SLUG: ReadonlyMap<string, DataAgentEntry> = new Map(
  DATA_AGENTS.map((a) => [a.slug, a]),
);

/** Convenience: agentId → icon. Mirrors the previous `AGENT_ICONS` const. */
export const AGENT_ICONS: Readonly<Record<string, LucideIcon>> = Object.freeze(
  Object.fromEntries(DATA_AGENTS.map((a) => [a.id, a.icon])),
);

/** Convenience: agentId → slug. */
export function agentIdToSlug(agentId: string): string | undefined {
  return DATA_AGENTS_BY_ID.get(agentId)?.slug;
}

/** Convenience: slug → agentId. */
export function slugToAgentId(slug: string): string | undefined {
  return DATA_AGENTS_BY_SLUG.get(slug)?.id;
}

/**
 * Data agents shown in the sidebar and on the Data overview for a given job.
 * Pre-appointment jobs only surface agents flagged `visibleOnPreAppointment`
 * (currently Corporate). Personal-insolvency jobs additionally drop any agent
 * flagged `hiddenForBankruptcy` (company-only analyses like board reports).
 */
export function dataAgentsForJob(
  jobStatus: string | undefined,
  appointmentType?: string,
): ReadonlyArray<DataAgentEntry> {
  const isBankruptcy =
    caseTypeFromAppointmentType(appointmentType) === "personal";
  return DATA_AGENTS.filter((agent) => {
    if (jobStatus === "pre_appointment" && !agent.visibleOnPreAppointment) {
      return false;
    }
    if (isBankruptcy && agent.hiddenForBankruptcy) return false;
    return true;
  });
}

/**
 * Whether a Data agent (by manifest id or URL slug) should be reachable on
 * this job. Unknown ids are hidden on pre-appointment jobs; agents flagged
 * `hiddenForBankruptcy` are hidden on personal-insolvency jobs.
 */
export function isDataAgentVisibleOnJob(
  agentIdOrSlug: string,
  jobStatus: string | undefined,
  appointmentType?: string,
): boolean {
  const entry =
    DATA_AGENTS_BY_ID.get(agentIdOrSlug) ??
    DATA_AGENTS_BY_SLUG.get(agentIdOrSlug);
  if (
    entry?.hiddenForBankruptcy &&
    caseTypeFromAppointmentType(appointmentType) === "personal"
  ) {
    return false;
  }
  if (jobStatus !== "pre_appointment") return true;
  return entry?.visibleOnPreAppointment === true;
}
